import React, {ChangeEvent} from 'react'
import {Box, Stack, styled, TextField} from '@mui/material'
import {TimelineViewConfig} from './TimelineViewConfig'
import {
  ChatAdapter,
  TimelineAction,
  TimelineActionManager,
  TimelineEventProducer
} from '@fnd/timeline'
import {WithTranslation, withTranslation} from 'react-i18next'
import SendMessageButton from './components/SendMessageButton'
import VoiceRecordButton from './components/VoiceRecordButton'

const InputWrapper = styled(Box)(({theme}) => ({
  paddingX: '1rem',
  paddingTop: '1rem',
  display: 'flex',
  flexGrow: 0,
  border: 0
}))

const ActionsWrapper = styled(Box)((props) => ({
  padding: '0.5rem 0',
  background: props.theme.palette.common.white
}))

const MessageFieldWrapper = styled(Stack)(({theme}) => ({
  width: '100%',
  border: `1px solid ${theme.palette.grey[300]}`,
  borderRadius: 30,
  paddingLeft: 10,
  paddingRight: 10,
  paddingBottom: 10,

  [theme.breakpoints.up('md')]: {
    width: '80%',
    margin: '0 auto'
  }
}))

interface TimelineMessageCreatorProps extends WithTranslation {
  actionManager: TimelineActionManager
  producer: TimelineEventProducer
  config: TimelineViewConfig
}

interface TimelineMessageCreatorState {
  message: string
  sending: boolean
  disabled: boolean
  actions: TimelineAction<any>[]
}

class TimelineMessageCreator extends React.Component<
  TimelineMessageCreatorProps,
  TimelineMessageCreatorState
> {
  state: TimelineMessageCreatorState = {
    message: '',
    disabled: false,
    sending: false,
    actions: []
  }
  ref = React.createRef<HTMLInputElement>()
  inputSending: boolean = false

  componentDidMount() {
    this.props.actionManager.subscribe((actions) => {
      this.setState({actions: actions})
    })
    this.props.actionManager.settings((settings) => {
      this.setState({disabled: settings.disabled})
    })
  }

  onSendMessage() {
    if (this.inputSending) {
      return
    }
    if (this.state.message.trim() === '') {
      return
    }
    this.inputSending = true
    this.setState({sending: true}, async () => {
      await this.props.producer.createEvent({
        message: this.state.message.trim(),
        adapterId: ChatAdapter.id
      })
      this.setState({message: '', sending: false}, () => {
        this.ref.current?.focus()
        this.inputSending = false
      })
    })
  }

  onUpdateMessage(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    this.setState({message: e.target.value})
  }

  onMessageEnter(e: React.KeyboardEvent) {
    if (e.key === 'Enter' && !e.shiftKey) {
      this.onSendMessage()
      e.preventDefault()
    }
  }

  handleAudioTranscribe = async (message: string) => {
    this.setState((prevState) => ({
      message: prevState.message ? `${prevState.message} ${message}` : message
    }))
  }

  renderMessageController() {
    return (
      <InputWrapper>
        <MessageFieldWrapper>
          {/* User message input */}
          <TextField
            fullWidth
            multiline
            maxRows={2}
            size="medium"
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  border: 'none'
                }
              }
            }}
            value={this.state.message}
            placeholder={
              this.state.disabled
                ? this.props.t(
                    'fnd-assistant-message-creator-placeholder-disabled'
                  )
                : this.props.t(
                    'fnd-assistant-message-creator-placeholder-enabled'
                  )
            }
            autoFocus={this.props.config.accessibility.messageCreator.autoFocus}
            inputRef={this.ref}
            disabled={this.state.sending || this.state.disabled}
            onChange={this.onUpdateMessage.bind(this)}
            onKeyDown={this.onMessageEnter.bind(this)}
          />

          {/* Message input buttons */}
          <Stack direction="row" spacing={1} justifyContent="space-between">
            <Stack direction="row"></Stack>
            <Stack direction="row" spacing={1}>
              <VoiceRecordButton onAudioRecorded={this.handleAudioTranscribe} />
              <SendMessageButton
                sending={this.state.sending}
                disabled={this.state.disabled}
                onClick={this.onSendMessage.bind(this)}
              />
            </Stack>
          </Stack>
        </MessageFieldWrapper>
      </InputWrapper>
    )
  }

  renderActionController() {
    if (this.state.actions.length > 0) {
      return (
        <ActionsWrapper>
          <Stack flexDirection={'row'} justifyContent={'center'} columnGap={1}>
            {this.state.actions.map((a) => {
              const El = a.node
              return (
                // @ts-ignore
                <El {...a.props} producer={this.props.producer} key={a.id} />
              )
            })}
          </Stack>
        </ActionsWrapper>
      )
    }
    return null
  }

  render() {
    return (
      <>
        {this.renderActionController()}
        {this.renderMessageController()}
      </>
    )
  }
}

export default withTranslation()(TimelineMessageCreator)
